import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {getFieldArrayFromObjectArrayByField, isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {ownQueueApi} from "./ownQueueApi";
import {createListFromArrayOfObjects} from "../../../../Utilities/Types/listUtilities";
import type { ListItem } from "../../../../Utilities/Types/listUtilities";
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullBleed} from "../../../../Components/Layout";
import {OwnQueueForm} from "./OwnQueueForm";
import {emptyOwnQueueAssignModel} from "./ownQueueFactory";
import {useInputForm} from "../../../../Components/Inputs";
import {filterQueueByStatus} from "./ownQueueUtility";
import {isRmtcAdmin} from "@/Utilities/LocalStorage/storageUtilities";
import {OwnQueueList} from "./OwnQueueList";
import {GridCell} from "../../../../Components/Grid";
import {SYSTEM_TYPES} from "../../libraryConstants";
import {getSystemTypeTextByValue} from "../../libraryUtility";

export const OwnQueueContainer = ({
                                      handleApiCall,
                                      setWarnings,
                                  }: ContextProps) => {

    const [ownQueue, setOwnQueue] = useState<Record<string, unknown>[]>([]);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));
    const [quantities, setQuantities] = useState<Record<string, number | undefined>>({});
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>();

    const ownLoanForm = useInputForm(emptyOwnQueueAssignModel);

    const pendingQueue = filterQueueByStatus(ownQueue, true);
    const isPendingQueueEmpty = isArrayNullOrEmpty(pendingQueue);
    const processingQueue = filterQueueByStatus(ownQueue, false);

    const sumQuantityForCatalogItem = (
        items: Record<string, any>[],
        ownCatalogItemId: unknown,
        getQuantity: (item: Record<string, any>) => number,
    ) =>
        items
            .filter((item) => String(item.ownCatalogItemId) === String(ownCatalogItemId))
            .reduce((total, item) => total + (getQuantity(item) || 0), 0);

    const isUncappedItem = (item: Record<string, any>) =>
        isRmtcAdmin() || Number(item.ownCatalogItem?.orderLimit ?? 0) <= 0;

    const getMaxQuantityForPendingItem = (pendingItem: Record<string, any>): number | undefined => {
        if (isUncappedItem(pendingItem)) return undefined;

        const catalogItemId = pendingItem.ownCatalogItemId;
        const orderLimit = Number(pendingItem.ownCatalogItem?.orderLimit ?? 0);

        const processingSum = sumQuantityForCatalogItem(
            processingQueue, catalogItemId, (item) => Number(item.quantity) || 0);

        const otherPendingSum = sumQuantityForCatalogItem(
            pendingQueue.filter((item) => item.ownQueueItemId !== pendingItem.ownQueueItemId),
            catalogItemId,
            (item) => quantities[String(item.ownQueueItemId)] ?? (Number(item.quantity) || 0));

        return Math.max(1, orderLimit - processingSum - otherPendingSum);
    };

    const handleQuantityChange = (ownQueueItemId: string, quantity: number | undefined) => {
        setQuantities((prev) => ({...prev, [ownQueueItemId]: quantity}));
    };

    const handleSelectAll = () => {
        const queueItems = getFieldArrayFromObjectArrayByField(pendingQueue, "ownQueueItemId");
        setSelectedItems(new Set(queueItems.map((id: unknown) => String(id))));
    }
    const handleDeselectAll = () => {
        setSelectedItems(new Set([]));    }

    const handleSelectedItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const item = event.target.value;
        const updatedSet = new Set(selectedItems || []);

        updatedSet.has(item)
            ? updatedSet.delete(item)
            : updatedSet.add(item);

        setSelectedItems(updatedSet);
    }

    const handleGetQueue = () => {
        handleApiCall(() => ownQueueApi.GetUserOwnQueueItems(), (results: Record<string, unknown>[]) => setOwnQueue(results));
    }

    const handleRemoveFromQueue = (ownQueuedItemId: string) => {
        if (confirm(`This will remove PERMANENTLY your request from the Queue. Are you sure you want to continue?`))
            handleApiCall(() => ownQueueApi.deleteOwnQueueItem(ownQueuedItemId), handleGetQueue);
    }

    const handleAssignOwnQueueItems = async (queueAssignViewModel: Record<string, unknown>) => {

        const selectedQueueItemIds = Array.from(selectedItems);

        const noItemsSelectedFromPendingQueue = isArrayNullOrEmpty(selectedQueueItemIds);
        if(noItemsSelectedFromPendingQueue) {
            setWarnings(["Please select at least one item from the queue to assign."]);
            return;
        }

        queueAssignViewModel.queueItems = selectedQueueItemIds.map((ownQueueItemId) => {
            const raw = quantities[ownQueueItemId];
            const quantity = Number.isFinite(raw) && (raw as number) >= 1 ? Math.floor(raw as number) : 1;
            return {ownQueueItemId, quantity};
        });

        handleApiCall(
            () => ownQueueApi.assignQueueItems(queueAssignViewModel),
            () => {
                handleGetQueue();
                setSelectedItems(new Set([]));
            },
        );
    }

    useEffect(() => {

        const getShippingLocations = () => {
            handleApiCall(() => ownQueueApi.getShippingLocations(), (results: Record<string, unknown>[]) => {
                setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
            });
        }

        if (!shippingLocations)
            getShippingLocations();

        handleGetQueue();
    }, []);

    useEffect(() => {
        const initialQuantities: Record<string, number | undefined> = {};
        filterQueueByStatus(ownQueue, true).forEach((item: Record<string, any>) => {
            initialQuantities[String(item.ownQueueItemId)] =
                isUncappedItem(item) ? undefined : Number(item.quantity) || 1;
        });
        setQuantities(initialQuantities);
    }, [ownQueue]);

    if (!shippingLocations || !ownLoanForm) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.Own.toString())} Queue`}/>

        <GridCell>
            {
                <p>
                    Please check with your school/organization to confirm that someone will be there to accept your
                    packages. Orders may take a month or more to arrive. All packages not received by your school or
                    organization will be returned to OWN.
                </p>
            }
        </GridCell>

        <LayoutFullBleed className={`catalog`}>
            {
                pendingQueue &&
                <OwnQueueList
                    ownQueue={pendingQueue}
                    handleDeselectAll={handleDeselectAll}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleSelectAll={handleSelectAll}
                    handleSelectedItemsChange={handleSelectedItemsChange}
                    selectedItems={selectedItems}
                    quantities={quantities}
                    getMaxQuantity={getMaxQuantityForPendingItem}
                    handleQuantityChange={handleQuantityChange}
                />
            }
            {
                !isPendingQueueEmpty &&
                <OwnQueueForm
                    ownLoanForm={ownLoanForm}
                    handleAssignOwnQueueItems={handleAssignOwnQueueItems}
                    setWarnings={setWarnings}
                    shippingLocations={shippingLocations}
                />
            }
            {
                processingQueue &&
                <OwnQueueList
                    ownQueue={processingQueue}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    isPendingQueue={false}
                />
            }
        </LayoutFullBleed>
    </>;
};
